import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { icon as fa_icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import FormButton from "../../components/form-components/FormButton.js";
import { QueryBuilder, formatQuery } from 'react-querybuilder';
import { parseCEL } from "react-querybuilder/parseCEL"
import { QueryBuilderDnD } from '@react-querybuilder/dnd';
import * as ReactDnD from 'react-dnd';
import * as ReactDndHtml5Backend from 'react-dnd-html5-backend';
import * as ReactDndTouchBackend from 'react-dnd-touch-backend';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import "./ModalVirtualTagEditor.css";
import Switch from "../../components/form-components/Switch.jsx";



// Sortable Rule Item Component
const SortableRuleItem = ({ rule, index, onQueryChange, onSetValueChange, groupingValues, isCollapsed, onToggleCollapse, onRemoveRule, canRemove }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: rule.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    return (
        <div 
            ref={setNodeRef} 
            style={style} 
            className={`sortable-rule-item ${isDragging ? 'dragging' : ''} ${isCollapsed ? 'collapsed' : ''}`}
        >
            <div className={`mt-0 flex flex-row items-center cursor-pointer rule-header ${isCollapsed ? 'collapsed' : ''}`} onClick={() => onToggleCollapse(rule.id)}>
                <div 
                    className="flex flex-row drag-handle"
                    {...attributes}
                    {...listeners}
                >
                    <FontAwesomeIcon
                        icon={fa_icon({ name: "grip-dots-vertical", family: "sharp", style: "solid" })}
                        className="w-6 h-6 text-text-3 mr-4 cursor-grab"
                    />
                </div>
                <div className="flex flex-row items-center flex-1">
                    <div className="text-text font-medium">
                        Rule {index + 1}
                    </div>
                    <button
                        className="ml-4 p-1 collapse-toggle"
                        aria-label={isCollapsed ? "Expand rule" : "Collapse rule"}
                    >
                        {isCollapsed ? (
                            <div className="flex flex-row">
                                <FontAwesomeIcon
                                    icon={fa_icon({ 
                                        name: "chevron-right", 
                                        family: "sharp", 
                                        style: "solid" 
                                    })}
                                    className="w-4 h-4 text-text-3 mt-1"
                                />
                                <div className="text-text font-medium ml-4">
                                    Set value to <span className="font-bold">{rule.setValue}</span>
                                </div>
                            </div>
                        ) : (
                            <FontAwesomeIcon
                                icon={fa_icon({ 
                                    name: "chevron-down", 
                                    family: "sharp", 
                                    style: "solid" 
                                })}
                                className="w-4 h-4 text-text-3"
                            />
                        )}
                    </button>
                </div>
                {canRemove && (
                    <button
                        className="ml-4 p-1 text-danger hover:text-danger"
                        onClick={(e) => {
                            e.stopPropagation();
                            onRemoveRule(rule.id);
                        }}
                        aria-label="Remove rule"
                    >
                        <FontAwesomeIcon
                            icon={fa_icon({ name: "trash", family: "sharp", style: "light" })}
                            className="w-4 h-4 mr-3"
                        />
                    </button>
                )}
            </div>
            <div className={`rule-content ${isCollapsed ? 'collapsed' : 'expanded'}`}>
                <div className="p-4 pt-0">
                    <QueryBuilderDnD dnd={{ ...ReactDnD, ...ReactDndHtml5Backend, ...ReactDndTouchBackend }}>
                        <QueryBuilder 
                            controlClassnames={{ queryBuilder: 'queryBuilder-braxnches queryBuilder-justified' }}
                            fields={groupingValues}
                            onQueryChange={(query) => onQueryChange(query, index)} 
                            query={rule.query}
                        />
                    </QueryBuilderDnD>
                    <div className="flex flex-row mt-4">
                        <div className="pt-[8px] mr-2">
                            Set value to
                        </div>
                        <input
                            type="text"
                            className="border-card-bd border rounded-ctrl p-2 w-[400px]"
                            value={rule.setValue}
                            onChange={(e) => onSetValueChange(e.target.value, index)}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

const ModalVirtualTagEditor = ({
	show,
    close,
	virtualTag,
    setVirtualTag,
    groupingValues,
    saveVirtualTag,
    isLoading = false,
    deleteVirtualTag,
    isDeleting = false
}) => {

    // console.log("ModalVirtualTagEditor - virtualTag", virtualTag);

    // a virtual tag consists of a name and one or more rules
    // each rule consists of a set value and a CEL query
    const [virtualTagRules, setVirtualTagRules] = useState([]);
    const [virtualTagName, setVirtualTagName] = useState("");
    const [collapsedRules, setCollapsedRules] = useState(new Set());
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    
    // Drag and drop sensors
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const createDefaultRule = () => ({
        id: Date.now() + Math.random(), // Unique ID for each rule
        setValue: "",
        query: {
            combinator: 'and',
            rules: [
              { field: 'firstName', operator: '=', value: '' },
            ],
        },
    })

    useEffect(() => {
        if (virtualTag && virtualTag.name) {
            setVirtualTagName(virtualTag.name);
        }

        if (virtualTag && virtualTag.rules && Array.isArray(virtualTag.rules)) {
            // Ensure each rule has an ID
            const rulesWithIds = virtualTag.rules.map(rule => {
                // Parse the query using parseCEL before adding the id
                const parsedQuery = typeof rule.query === "string" ? parseCEL(rule.query) : rule.query;
                return {
                    ...rule,
                    query: parsedQuery,
                    id: rule.id || Date.now() + Math.random()
                };
            });
            setVirtualTagRules(rulesWithIds);
            
            // If there is more than one rule, collapse them all by default
            if (rulesWithIds.length > 1) {
                const allRuleIds = new Set(rulesWithIds.map(rule => rule.id));
                setCollapsedRules(allRuleIds);
            } else {
                setCollapsedRules(new Set());
            }
        } else {
            setVirtualTagRules([createDefaultRule()]);
            setCollapsedRules(new Set());
        }
    }, [virtualTag]);


    const handleChange = (e) => {
        setVirtualTagName(e.target.value);
    }

    const applyChanges = () => {
        // Format the query for the CEL expression
        const updatedVirtualTag = { 
            ...virtualTag, 
            name: virtualTagName, 
            rules: virtualTagRules.map(rule => ({ ...rule, query: formatQuery(rule.query, 'cel') })),
            active: virtualTag?.active ?? true
        };    
        setVirtualTag(updatedVirtualTag);
        saveVirtualTag(updatedVirtualTag);
    }

    const handleQueryChange = (query, index) => {
        const newRules = [...virtualTagRules];
        newRules[index].query = query;
        setVirtualTagRules(newRules);
    }

    const handleSetValueChange = (value, index) => {
        const newRules = [...virtualTagRules];
        newRules[index].setValue = value;
        setVirtualTagRules(newRules);
    }

    const addRule = () => {
        const newRules = [...virtualTagRules];
        const newRule = createDefaultRule();
        newRules.push(newRule);
        setVirtualTagRules(newRules);
        // Ensure new rules start expanded (not in collapsed set)
        setCollapsedRules(prev => {
            const newSet = new Set(prev);
            newSet.delete(newRule.id); // Remove from collapsed set if it was there
            return newSet;
        });
    }

    const removeRule = (ruleId) => {
        // Prevent removing the last rule
        if (virtualTagRules.length <= 1) {
            return;
        }
        
        const newRules = virtualTagRules.filter(rule => rule.id !== ruleId);
        setVirtualTagRules(newRules);
        
        // Remove from collapsed set if it was there
        setCollapsedRules(prev => {
            const newSet = new Set(prev);
            newSet.delete(ruleId);
            return newSet;
        });
    }

    const handleDragEnd = (event) => {
        const { active, over } = event;

        if (active.id !== over.id) {
            setVirtualTagRules((items) => {
                const oldIndex = items.findIndex((item) => item.id === active.id);
                const newIndex = items.findIndex((item) => item.id === over.id);

                return arrayMove(items, oldIndex, newIndex);
            });
        }
    }

    const toggleRuleCollapse = (ruleId) => {
        setCollapsedRules(prev => {
            const newSet = new Set(prev);
            if (newSet.has(ruleId)) {
                newSet.delete(ruleId);
            } else {
                newSet.add(ruleId);
            }
            return newSet;
        });
    }

    
    const handleDeleteClick = () => {
        setShowDeleteConfirmation(true);
    }

    const handleDeleteConfirm = () => {
        if (deleteVirtualTag && virtualTag && virtualTag.id) {
            deleteVirtualTag();
        }
        setShowDeleteConfirmation(false);
    }

    const handleDeleteCancel = () => {
        setShowDeleteConfirmation(false);
    }

	const modalStyle = "bg-black bg-opacity-50 z-[9999] fixed grid content-center place-content-center items-center top-0 left-0 right-0 w-full p-4 overflow-x-hidden overflow-y-auto md:inset-0 h-[calc(100%-1rem)] max-h-full cursor-default text-text";
	
    return ReactDOM.createPortal(
		<>
			{show ? (
				<div
					className={modalStyle}
				>
					<div
						className="w-[1100px] h-[900px] bg-card relative border-card-bd border rounded-card flex flex-col z-[10000]"
						onClick={(e) => e.stopPropagation()}
					>
						{/* Header */}
						<div className="flex flex-row justify-between items-start p-4 border-border">
                            <div className="flex flex-col ml-6 mt-2">
                                <div className="text-text-3 uppercase text-sm">Virtual Tags</div>
                                <div className="text-text text-2xl font-semibold" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Change the virtual tag</div>
                            </div>
                            
                            {/* Close button */}
                            <button
                                onClick={() => close()}
                                aria-label="Close dialog"
                                className="cursor-pointer"
                            >
                                <FontAwesomeIcon
                                    icon={fa_icon({ name: "xmark", family: "sharp", style: "solid" })}
                                    className="w-6 h-6 text-text-3 text-[20px] mr-2 mt-1"
                                />
                            </button>
						</div>

						{/* Body */}
						<div className="flex-1 p-4 overflow-y-auto bg-surface-2 pl-10">
							<div className="w-full">
								<div className="flex flex-row justify-between">
                                    <div className="flex flex-col">
                                        <div className="text-text">
                                            Tag name
                                        </div>
                                        <input
                                            type="text"
                                            className="border-card-bd border rounded-ctrl p-2 w-[400px]"
                                            value={virtualTagName}
                                            onChange={(e) => handleChange(e)}
                                        />
                                    </div>
                                    <div className="flex flex-col justify-center items-center ml-4 mr-4">
                                    <label htmlFor="notifications" className="flex flex-row items-end">
                                        <div className="text-text mr-2">Virtual tag is active</div>
                                        <Switch id="notifications" 
                                            checked={virtualTag?.active ?? true}
                                            onCheckedChange={(checked) => {
                                                setVirtualTag({
                                                    ...virtualTag,
                                                    active: checked
                                                });
                                            }}
                                        />
                                    </label>
                                    </div>
                                </div>
                                
                                {/* Add Rule Button */}
                                <div className="mt-8 flex flex-row">
                                    <div className="ml-auto mr-8 cursor-pointer" onClick={addRule}>
                                        + Value
                                    </div>
                                </div>

                                {/* Drag and Drop Rules Container */}
                                <DndContext
                                    sensors={sensors}
                                    collisionDetection={closestCenter}
                                    onDragEnd={handleDragEnd}
                                >
                                    <SortableContext
                                        items={virtualTagRules.map(rule => rule.id)}
                                        strategy={verticalListSortingStrategy}
                                    >
                                        {(virtualTagRules || []).map((rule, index) => (
                                            <SortableRuleItem
                                                key={rule.id}
                                                rule={rule}
                                                index={index}
                                                onQueryChange={handleQueryChange}
                                                onSetValueChange={handleSetValueChange}
                                                groupingValues={groupingValues}
                                                isCollapsed={collapsedRules.has(rule.id)}
                                                onToggleCollapse={toggleRuleCollapse}
                                                onRemoveRule={removeRule}
                                                canRemove={virtualTagRules.length > 1}
                                            />
                                        ))}
                                    </SortableContext>
                                </DndContext>
							</div>
						</div>

						{/* Footer */}
						<div className="flex justify-between items-center py-5 px-4 ml-4">
							{/* Apply and Cancel buttons */}
							<div className="flex gap-2">
								<FormButton
									title={isLoading ? "Saving..." : "Apply changes"}
									style="primary"
									disabled={isLoading || isDeleting}
									onClick={() => {
										applyChanges();
									}}
								/>

								<FormButton
									style="secondary"
									title="Cancel"
									onClick={() => close()}
								/>
							</div>

							{/* Delete button - only show for existing virtual tags */}
							{virtualTag && virtualTag.id ? (
                                <div className="flex gap-2">
                                    <FormButton
                                        title={isDeleting ? "Deleting..." : "Delete virtual tag"}
                                        style="danger"
                                        disabled={isDeleting || isLoading}
                                        onClick={handleDeleteClick}
                                    />
                                </div>
                            ) : (
								<div></div>
							)}
						</div>
					</div>
				</div>
			) : null}

			{/* Delete Confirmation Dialog */}
			{showDeleteConfirmation && (
				<div className="bg-black bg-opacity-50 z-[9999] fixed grid content-center place-content-center items-center top-0 left-0 right-0 w-full p-4 overflow-x-hidden overflow-y-auto md:inset-0 h-[calc(100%-1rem)] max-h-full cursor-default text-text">
					<div className="w-[400px] bg-card relative border-card-bd border rounded-card flex flex-col z-[10000]">
						{/* Header */}
						<div className="flex flex-row justify-between items-start p-4 border-border">
							<div className="flex flex-col ml-4 mt-2">
								<div className="text-text-3 uppercase text-sm">Confirm Delete</div>
								<div className="text-text text-xl font-semibold" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
									Delete Virtual Tag
								</div>
							</div>
							
							{/* Close button */}
							<button
								onClick={handleDeleteCancel}
								aria-label="Close dialog"
								className="cursor-pointer"
							>
								<FontAwesomeIcon
									icon={fa_icon({ name: "xmark", family: "sharp", style: "solid" })}
									className="w-6 h-6 text-text-3 text-[20px] mr-2 mt-1"
								/>
							</button>
						</div>

						{/* Body */}
						<div className="p-6">
							<p className="text-text mb-4">
								Are you sure you want to delete the virtual tag <strong>"{virtualTagName}"</strong>? 
								This action cannot be undone.
							</p>
						</div>

						{/* Footer */}
						<div className="flex justify-start gap-2 py-5 pl-4 pr-4">
                        {virtualTag && virtualTag.id && (
								<FormButton
									title={isDeleting ? "Deleting..." : "Delete"}
									style="danger"
									disabled={isDeleting}
									onClick={handleDeleteConfirm}
								/>
							)}

							<FormButton
								title="Cancel"
								onClick={handleDeleteCancel}
							/>
						</div>
					</div>
				</div>
			)}
		</>,
		document.body
	);
};

export default ModalVirtualTagEditor;
